// Shared column registry for the Deliveries surfaces — the on-screen grid
// (Deliveries.tsx "Table" view) and the print report (DeliveriesReport.tsx).
//
// One source of truth for header label + cell value + alignment so the grid,
// the printed table, and the CSV export never drift apart. Mirrors the
// TransportRecon LINE_COLUMNS pattern, reused across two pages:
//   • the grid auto-hides columns empty for every row (like PalletTable)
//   • the report lets the operator pick columns via checkboxes
// Both read the same `value(row)` projection.

import type { Delivery } from '@/types/database'
import { intakePathLabel } from '@/lib/intakePath'
import { CONTAINER_LABEL, type ContainerType } from '@/lib/containerTypes'

// Status labels — includes the ALL / PALTRACK_PENDING filter pseudo-statuses
// so the report's filter-summary chips can reuse the same map.
export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  ALL:               'All statuses',
  RECEIVED:          'Received',
  AWAITING_DRENCH:   'Awaiting Drench',
  DRENCHED:          'Drenched',
  OUT_FOR_COLOUR_UP: 'Out for Colour-Up',
  IN_STOCK:          'In Stock',
  TIPPED:            'Tipped',
  PALTRACK_PENDING:  'Paltrack pending',
  NEEDS_CHECK:       'Needs check',
}

export type DeliveryColId =
  | 'received' | 'dn' | 'client_ref' | 'grower' | 'variety' | 'puc'
  | 'block' | 'delivery_point' | 'vehicle' | 'driver' | 'intake' | 'status'
  | 'bin_type' | 'bins' | 'remaining' | 'tipped_kg'

// Flattened delivery row: the Delivery plus the derived fields the columns
// need (tipped kg from delivery_tipping_summary, the combined+deduped DN list).
export interface DeliveryRow {
  delivery_id:   string
  received_at:   string
  dn_numbers:    string[]
  client_ref_no: string | null
  grower:        string
  variety:       string
  puc:           string | null
  block:         string | null
  /** Point NAME snapshot (server-stamped). NULL for citrus and every legacy row. */
  delivery_point: string | null
  vehicle_reg:   string | null
  driver_name:   string | null
  intake_path:   string | null
  status:        string
  bin_type:      string | null
  bins_received: number
  bins_remaining: number
  kg_tipped:     number
}

export interface DeliveryColumnDef {
  id:     DeliveryColId
  label:  string
  align?: 'right'
  mono?:  boolean
  // Plain-text cell value, or null when this row has no value. Null drives the
  // grid's auto-hide-empty-column rule; the report renders it as an em-dash.
  // Multi-line values (DN list) join with "\n" — render with whitespace-pre-line.
  value:  (r: DeliveryRow) => string | null
  // CSV projection. Defaults to value() with newlines flattened; numeric
  // columns override to emit a raw number so spreadsheets sum them.
  csv?:   (r: DeliveryRow) => string | number
}

// SAST-anchored DD Mon YYYY, matching the existing report formatter.
function fmtDate(iso: string | null): string | null {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}
const num = (n: number | null | undefined) => (n == null ? null : n.toLocaleString('en-ZA'))

export const DELIVERY_COLUMNS: DeliveryColumnDef[] = [
  { id: 'received',   label: 'Received',   value: r => fmtDate(r.received_at) },
  { id: 'dn',         label: 'DN #',       mono: true,
    value: r => (r.dn_numbers.length ? r.dn_numbers.join('\n') : null),
    csv:   r => r.dn_numbers.join(' / ') },
  { id: 'client_ref', label: 'Client ref', mono: true, value: r => r.client_ref_no },
  { id: 'grower',     label: 'Grower',     value: r => r.grower },
  { id: 'variety',    label: 'Variety',    value: r => r.variety },
  { id: 'puc',        label: 'PUC',        value: r => r.puc },
  { id: 'block',      label: 'Block',      value: r => r.block },
  // Null for citrus/legacy — the grid auto-hides an all-null column and the
  // report drops it on the same data-presence rule, so nothing changes for a
  // packhouse that defines no points.
  { id: 'delivery_point', label: 'Delivery point', value: r => r.delivery_point },
  { id: 'vehicle',    label: 'Vehicle',    mono: true, value: r => r.vehicle_reg },
  { id: 'driver',     label: 'Driver',     value: r => r.driver_name },
  { id: 'intake',     label: 'Intake',     value: r => (r.intake_path ? intakePathLabel(r.intake_path) : null) },
  { id: 'status',     label: 'Status',     value: r => DELIVERY_STATUS_LABELS[r.status] ?? r.status },
  { id: 'bin_type',   label: 'Bin type',   value: r => r.bin_type },
  { id: 'bins',       label: 'Bins',       align: 'right', value: r => num(r.bins_received),  csv: r => r.bins_received },
  { id: 'remaining',  label: 'Remaining',  align: 'right', value: r => num(r.bins_remaining), csv: r => r.bins_remaining },
  { id: 'tipped_kg',  label: 'Tipped kg',  align: 'right',
    value: r => (r.kg_tipped > 0 ? Math.round(r.kg_tipped).toLocaleString('en-ZA') : null),
    csv:   r => Math.round(r.kg_tipped) },
]

// Numeric columns that participate in the report's Totals row.
export const DELIVERY_TOTAL_COLS = new Set<DeliveryColId>(['bins', 'remaining', 'tipped_kg'])

// Flatten a Delivery (+ its tipped-kg) into a DeliveryRow. delivery_note_number,
// client_ref_no and the two fruit-container columns live on the row at runtime
// (select '*') but aren't on the generated Delivery type, so they're read via a
// narrow cast.
//
// binTypesById (bin_type_id → label, from useBinTypes) resolves a custom/grower
// bin type. A delivery carries EITHER fruit_container_type (fixed enum) OR
// fruit_bin_type_id (custom) — never both. Without the map the custom case
// falls back to em-dash, so callers that render the "Bin type" column should
// pass it.
export function toDeliveryRow(
  d: Delivery,
  kgTipped: number,
  binTypesById?: Map<string, string>,
): DeliveryRow {
  const ext = d as Delivery & {
    delivery_note_number?: string | null
    client_ref_no?:        string | null
    fruit_container_type?: ContainerType | null
    fruit_bin_type_id?:    string | null
  }
  // Combine the primary DN# with any added via top-ups (delivery_additions),
  // de-duplicated so a re-recorded primary doesn't appear twice.
  const dn: string[] = []
  if (ext.delivery_note_number) dn.push(ext.delivery_note_number)
  for (const add of d.delivery_additions ?? []) {
    if (add.delivery_note_number && !dn.includes(add.delivery_note_number)) dn.push(add.delivery_note_number)
  }
  const binType = ext.fruit_bin_type_id
    ? (binTypesById?.get(ext.fruit_bin_type_id) ?? null)
    : ext.fruit_container_type
      ? (CONTAINER_LABEL[ext.fruit_container_type] ?? null)
      : null
  return {
    delivery_id:    d.delivery_id,
    received_at:    d.received_at,
    dn_numbers:     dn,
    client_ref_no:  ext.client_ref_no ?? null,
    grower:         d.grower,
    variety:        d.variety,
    puc:            d.puc,
    block:          d.block,
    delivery_point: d.delivery_point,
    vehicle_reg:    d.vehicle_reg,
    driver_name:    d.driver_name,
    intake_path:    d.intake_path,
    status:         d.status,
    bin_type:       binType,
    bins_received:  d.bins_received,
    bins_remaining: d.bins_remaining,
    kg_tipped:      kgTipped,
  }
}

// CSV cell for a column, flattening multi-line display values.
export function deliveryCsvCell(c: DeliveryColumnDef, r: DeliveryRow): string | number {
  if (c.csv) return c.csv(r)
  const v = c.value(r)
  return v == null ? '' : v.replace(/\n/g, ' / ')
}
